import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useCashRegister } from './hooks/useCashRegister';
import { DailySummary } from './components/DailySummary';
import { DailyExpenses } from './components/DailyExpenses';
import { CashCountForm } from './components/CashCountForm';
import { FinalBalance } from './components/FinalBalance';
import { ExpenseModal } from './components/ExpenseModal';
import { CashRegisterOpening } from './components/CashRegisterOpening';
import { ShiftChangeWarningModal } from '@/components/ShiftChangeWarningModal';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { formatDateForInput, formatDatePeru } from '@/utils/dateUtils';

export const CashRegisterPage: React.FC = () => {
  const { selectedLocation, user } = useAuth();
  const locationId = selectedLocation?.id || user?.locationId || 'location-1';
  // Fixed: Use local date instead of UTC to avoid timezone issues
  const date = formatDateForInput(new Date()); // YYYY-MM-DD format in Peru timezone
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showShiftChangeWarning, setShowShiftChangeWarning] = useState(false);
  const [shiftWarningDismissed, setShiftWarningDismissed] = useState(false);

  const {
    state,
    updateCashCount,
    saveCashCount,
    openExpenseModal,
    closeExpenseModal,
    openIncomeModal,
    closeIncomeModal,
    addExpense,
    deleteExpense,
    openCashRegister,
    closeCashRegister,
  } = useCashRegister(locationId, date);

  // Actualizar la hora cada minuto
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(timer);
  }, []);

  // Detectar cambio de turno y mostrar modal
  useEffect(() => {
    const currentShift = state.dailySummary?.shift || state.currentShift;
    const actualShift = state.actualShift;

    // Mostrar modal si hay cambio de turno, la caja está abierta y no se ha descartado
    if (
      state.isOpened &&
      currentShift &&
      actualShift &&
      currentShift !== actualShift &&
      actualShift !== 'CERRADO' &&
      !shiftWarningDismissed
    ) {
      setShowShiftChangeWarning(true);
    }
  }, [state.isOpened, state.dailySummary?.shift, state.currentShift, state.actualShift, shiftWarningDismissed]);

  const handleDismissShiftWarning = () => {
    setShowShiftChangeWarning(false);
    setShiftWarningDismissed(true);
  };

  const handleOpenCashRegister = async (openData: any) => {
    try {
      await openCashRegister(openData);
      toast.success('¡Caja abierta exitosamente! Puedes comenzar las operaciones del día.');
    } catch (error: any) {
      if (error.message && error.message.includes('ya está abierta')) {
        // The hook should have already handled the transition to opened state
        // So we don't need to show an error, just a success message
        toast.info('La caja ya estaba abierta. Continuando con el cierre del día.');
      } else {
        toast.error('Error al abrir la caja. Inténtalo de nuevo.');
        throw error;
      }
    }
  };

  const handleCloseCashRegister = () => {
    if (!state.finalBalance?.canClose) {
      toast.error('No se puede cerrar la caja. La diferencia es muy alta.');
      return;
    }
    
    // Mostrar modal de confirmación
    setShowCloseConfirmation(true);
  };

  const handleConfirmClose = async () => {
    try {
      setShowCloseConfirmation(false);
      await closeCashRegister();
      toast.success('¡Caja cerrada exitosamente! Puedes abrir una nueva caja cuando sea necesario.');
      
      // El hook automáticamente reseteará el estado y mostrará la pantalla de apertura
    } catch (error) {
      toast.error('Error al cerrar la caja. Inténtalo de nuevo.');
      setShowCloseConfirmation(false);
    }
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  if (state.loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos del día...</p>
        </div>
      </div>
    );
  }

  // If cash register is not opened, show opening form
  if (!state.isOpened) {
    return (
      <CashRegisterOpening
        onOpen={handleOpenCashRegister}
        loading={state.loading}
        locationId={locationId}
        date={date}
        currentShift={state.currentShift}
        shiftSchedule={state.shiftSchedule}
      />
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Caja</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Gestión de caja - {(state.dailySummary?.shift || state.currentShift) && (
              <span className="font-bold">Turno: {state.dailySummary?.shift || state.currentShift}</span>
            )}
            {' · '}
            <span className="font-medium text-gray-700">
              {currentTime.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
            </span>
            {state.actualShift && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                Horario: {state.actualShift}
              </span>
            )}
          </p>
        </div>

        <Button
          onClick={handleCloseCashRegister}
          disabled={!state.finalBalance?.canClose || state.loading}
          variant="destructive"
          size="xl"
          className="w-full sm:w-auto"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {state.loading ? 'Cerrando...' : 'Cerrar Caja'}
        </Button>
      </div>

      {/* Main Content - 3 Tabs */}
      <Tabs defaultValue="info" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 h-14 bg-gray-100 rounded-xl p-1">
          <TabsTrigger
            value="info"
            className="h-12 rounded-lg text-base font-semibold transition-all duration-200 data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-orange-100 text-gray-700"
          >
            Información de Caja
          </TabsTrigger>
          <TabsTrigger
            value="movements"
            className="h-12 rounded-lg text-base font-semibold transition-all duration-200 data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-blue-100 text-gray-700"
          >
            Movimientos
          </TabsTrigger>
          <TabsTrigger
            value="cash-count"
            className="h-12 rounded-lg text-base font-semibold transition-all duration-200 data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-green-100 text-gray-700"
          >
            Conteo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4 md:space-y-6">
          {state.dailySummary && (
            <DailySummary
              data={state.dailySummary}
              expenses={state.expenses}
            />
          )}
        </TabsContent>

        <TabsContent value="movements" className="space-y-4 md:space-y-6">
          <DailyExpenses
            expenses={state.expenses}
            onAddExpense={openExpenseModal}
            onAddIncome={openIncomeModal}
            onDeleteExpense={deleteExpense}
          />
        </TabsContent>

        <TabsContent value="cash-count" className="space-y-4 md:space-y-6">
          <CashCountForm
            cashCount={state.cashCount}
            onChange={updateCashCount}
            onSave={saveCashCount}
          />

          {state.finalBalance && (
            <FinalBalance
              balance={state.finalBalance}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de gastos */}
      <ExpenseModal
        isOpen={state.isExpenseModalOpen}
        onClose={closeExpenseModal}
        onSubmit={addExpense}
        type="expense"
        locationId={locationId}
      />
      
      {/* Modal de ingresos */}
      <ExpenseModal
        isOpen={state.isIncomeModalOpen}
        onClose={closeIncomeModal}
        onSubmit={addExpense}
        type="income"
        locationId={locationId}
      />

      {/* Modal de confirmación de cierre */}
      {showCloseConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    ¿Confirmar cierre de caja?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Esta acción cerrará la caja del turno actual y no se podrá deshacer.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Turno:</span>
                  <span className="text-sm font-semibold text-blue-600">
                    {state.dailySummary?.shift || state.currentShift}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Fecha:</span>
                  <span className="text-sm text-gray-900">
                    {formatDatePeru(date)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Saldo inicial:</span>
                  <span className="text-sm text-gray-900">
                    S/ {(state.dailySummary?.openingAmount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Ventas en efectivo:</span>
                  <span className="text-sm text-gray-900">
                    S/ {(state.dailySummary?.cashSales || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2 pt-2 border-t">
                  <span className="text-sm font-bold text-gray-800">Saldo Final:</span>
                  <span className="text-sm font-bold text-blue-600">
                    S/ {((state.dailySummary?.openingAmount || 0) + (state.dailySummary?.cashSales || 0) +
                        state.expenses.reduce((acc, exp) =>
                          exp.type === 'income' ? acc + exp.amount : acc - exp.amount, 0
                        )).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Total contado:</span>
                  <span className="text-sm font-semibold text-green-600">
                    S/ {(state.finalBalance?.countedCash || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Diferencia:</span>
                  <span className={`text-sm font-semibold ${
                    (state.finalBalance?.difference || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    S/ {(state.finalBalance?.difference || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCancelClose}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmClose}
                  variant="destructive"
                  className="flex-1"
                  disabled={state.loading}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {state.loading ? 'Cerrando...' : 'Cerrar Caja'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de advertencia de cambio de turno */}
      <ShiftChangeWarningModal
        isOpen={showShiftChangeWarning}
        currentShift={state.dailySummary?.shift || state.currentShift || ''}
        actualShift={state.actualShift || ''}
        onClose={handleDismissShiftWarning}
      />
    </div>
  );
};